<template>
  <nav class="admin-tabs" ref="navRef">
    <div class="admin-tab-slider" :class="{ 'is-animating': isAnimating }" :style="sliderStyle" />
    <router-link to="/manage-cards" class="admin-tab" :class="{ active: route.path === '/manage-cards' }" :ref="el => tabEls[0] = el?.$el ?? el">
      Cards
    </router-link>
    <router-link to="/manage-departments" class="admin-tab" :class="{ active: route.path === '/manage-departments' }" :ref="el => tabEls[1] = el?.$el ?? el">
      Departments
    </router-link>
    <router-link to="/game-users" class="admin-tab" :class="{ active: route.path === '/game-users' }" :ref="el => tabEls[2] = el?.$el ?? el">
      Users
    </router-link>
    <router-link v-if="showStats" to="/statistics" class="admin-tab" :class="{ active: route.path === '/statistics' }" :ref="el => tabEls[3] = el?.$el ?? el">
      Statistics
    </router-link>
  </nav>
</template>

<script setup>
import { useRoute } from 'vue-router'
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useFeatureFlags } from '@/composables/useFeatureFlags'

const SLIDER_CACHE_KEY = '__adminTabsSliderState'
const TRANSITION_MS = 280

const { isEnabled } = useFeatureFlags()
const showStats = isEnabled('USER_STATS')

const route = useRoute()
const navRef = ref(null)
const tabEls = [null, null, null, null]
const tabs = ['/manage-cards', '/manage-departments', '/game-users']
if (showStats) tabs.push('/statistics')

function readCache() {
  if (typeof window === 'undefined') return null
  return window[SLIDER_CACHE_KEY] ?? null
}
function writeCache(state) {
  if (typeof window === 'undefined') return
  window[SLIDER_CACHE_KEY] = state
}

const cached = readCache()
const sliderStyle = ref({
  left: cached ? `${cached.left}px` : '4px',
  width: cached ? `${cached.width}px` : '0px',
})
const isAnimating = ref(false)

function getBoundsForPath(path) {
  const tabIndex = tabs.indexOf(path)
  const tabEl = tabEls[tabIndex]
  const navEl = navRef.value
  if (!tabEl || !navEl) return null
  const navRect = navEl.getBoundingClientRect()
  const tabRect = tabEl.getBoundingClientRect()
  return {
    left: tabRect.left - navRect.left,
    width: tabRect.width,
  }
}

function setBounds(bounds) {
  sliderStyle.value = { left: `${bounds.left}px`, width: `${bounds.width}px` }
}

let settleTimer = null

function animateTo(bounds) {
  if (settleTimer) {
    clearTimeout(settleTimer)
    settleTimer = null
  }
  isAnimating.value = true
  setBounds(bounds)
  settleTimer = window.setTimeout(() => {
    isAnimating.value = false
    settleTimer = null
  }, TRANSITION_MS + 20)
}

function handleResize() {
  const b = getBoundsForPath(route.path)
  if (b) setBounds(b)
}

onMounted(() => {
  nextTick(() => {
    const targetBounds = getBoundsForPath(route.path)
    if (!targetBounds) return

    const startedFromCache =
      cached && (cached.left !== targetBounds.left || cached.width !== targetBounds.width)

    if (startedFromCache) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          animateTo(targetBounds)
        })
      })
    } else {
      setBounds(targetBounds)
    }

    window.addEventListener('resize', handleResize)
  })
})

onBeforeUnmount(() => {
  const left = parseFloat(sliderStyle.value.left)
  const width = parseFloat(sliderStyle.value.width)
  if (!Number.isNaN(left) && !Number.isNaN(width) && width > 0) {
    writeCache({ left, width })
  }
  if (settleTimer) clearTimeout(settleTimer)
  window.removeEventListener('resize', handleResize)
})
</script>

<style lang="scss" src="@/styles/_adminTabs.scss"></style>
